import BigNumber from 'bignumber.js';

import { IBlockchainEntity } from './common';

export type ChainPath = string;

export enum ChainType {
  Mainnet = 'mainnet',
  Testnet = 'testnet',
  Devnet = 'devnet',
  Signet = 'signet',
}

export enum ChainSubType {
  Athens3 = 'athens3',
}

export enum ESortChainsType {
  Trending = 'trending',
  NameAZ = 'name-a-z',
  NameZA = 'name-z-a',
  UsageHighLow = 'usage-high-low',
  UsageLowHigh = 'usage-low-high',
  StatusHighLow = 'status-high-low',
  StatusLowHigh = 'status-low-high',
}

export enum Timeframe {
  Hour,
  Day,
  Week,
  Month,
}

export interface ChainURL {
  enterprise: string;
  enterpriseWs: string;
  grpc?: string;
  rest?: string;
  rpc: string;
  ws?: string;
}

export interface EnrichedChainURL extends ChainURL {
  name: string;
  id: string;
}

type MainBlockchainEntity = Exclude<
  IBlockchainEntity,
  'extends' | 'features' | 'id'
>;

export interface ChainBadge extends Chain {
  isSelected: boolean;
}

export interface Chain extends MainBlockchainEntity {
  id: ChainID;
  isArchive?: boolean;
  isComingSoon: boolean;
  isMainnetComingSoon?: boolean;
  isMainnetPremiumOnly?: boolean;
  extenders?: Chain[];
  extensions?: Chain[];
  urls: ChainURL[];
  totalRequests?: BigNumber;
  chainExtends?: string;
  beacons?: Chain[];
  opnodes?: Chain[];
  vrpcs?: Chain[];
  testnets?: Chain[];
  devnets?: Chain[];
  signets?: Chain[];
  hasWSFeature: boolean;
  hasRESTFeature: boolean;
  hasRPCFeature: boolean;
  hasEnterpriseFeature: boolean;
  hasGRPCFeature: boolean;
}

export type GroupedBlockchainType = Record<string, Chain[]>;

export enum ChainID {
  ALL_CHAINS = 'all_chains',

  ZERO_G = '0g',
  ZERO_G_EVM = '0g-evm',
  ZERO_G_BEACONKIT_REST = '0g_beaconkit-rest',
  ZERO_G_GALILEO_TESTNET = '0g_galileo',
  ZERO_G_GALILEO_BEACONKIT_REST = '0g_galileo-beaconkit-rest',
  ZERO_G_GALILEO_EVM = '0g_galileo-evm',
  ZERO_G_GALILEO_TENDERMINT_REST = '0g_galileo-tendermint-rest',
  ZERO_G_GALILEO_TENDERMINT_RPC = '0g_galileo-tendermint-rpc',

  ABCHAIN = 'abchain',
  ABCHAIN_TESTNET = 'abchain-testnet',
  ALLORA = 'allora',
  ALLORA_MAINNET_REST = 'allora_mainnet-rest',
  ALLORA_MAINNET_RPC = 'allora_mainnet-rpc',
  ALLORA_MAINNET_COSMOS_REST = 'allora_mainnet-cosmos-rest',
  ALLORA_MAINNET_GRPC = 'allora_mainnet-grpc',
  ALLORA_TESTNET = 'allora_testnet',
  ALLORA_TESTNET_REST = 'allora_testnet-rest',
  ALLORA_TESTNET_RPC = 'allora_testnet-rpc',
  ALLORA_TESTNET_COSMOS_REST = 'allora_testnet-cosmos-rest',
  ALLORA_TESTNET_GRPC = 'allora_testnet-grpc',
  APTOS = 'aptos',
  ARBITRUM = 'arbitrum',
  ARBITRUM_TESTNET = 'arbitrum_testnet',
  ARBITRUM_SEPOLIA = 'arbitrum_sepolia',
  ARBITRUM_NOVA = 'arbitrumnova',
  AVALANCHE = 'avalanche',
  AVALANCHE_C = 'avalanche-c',
  AVALANCHE_EVM = 'avalanche-evm',
  AVALANCHE_FUJI = 'avalanche_fuji',
  AVALANCHE_FUJI_C = 'avalanche_fuji-c',
  AVALANCHE_FUJI_EVM = 'avalanche_fuji-evm',
  AVALANCHE_FUJI_P = 'avalanche_fuji-p',
  AVALANCHE_FUJI_X = 'avalanche_fuji-x',
  AVALANCHE_P = 'avalanche-p',
  AVALANCHE_X = 'avalanche-x',
  ATLETA = 'atleta',
  ATLETA_OLYMPIA = 'atleta_olympia',

  AVAIL = 'avail',
  AVAIL_GOLDBERG_TESTNET = 'avail_goldberg_testnet',
  AVAIL_TURING_TESTNET = 'avail_turing_testnet',

  BAHAMUT = 'bahamut',
  BAHAMUT_OCEAN = 'bahamut_ocean',
  BAHAMUT_HORIZON = 'bahamut_horizon',

  BSC = 'bsc',
  BSC_TESTNET_CHAPEL = 'bsc_testnet_chapel',

  BASE = 'base',
  BASE_SEPOLIA = 'base_sepolia',
  BASE_TESTNET = 'base_goerli',

  BERACHAIN = 'berachain',
  BERACHAIN_TESTNET = 'berachain_testnet',
  BERACHAIN_GUARDED_TESTNET = 'berachain-guarded-testnet',
  BERACHAIN_GUARDED_TESTNET_EVM = 'berachain-guarded-testnet-evm',

  BOTANIX = 'botanix',
  BOTANIX_TESTNET = 'botanix-testnet',

  BLAST = 'blast',
  BLAST_TESTNET_SEPOLIA = 'blast_testnet_sepolia',

  B2 = 'b2',
  B2_HABITAT_TESTNET = 'b2_habitat_testnet',
  BTTC = 'bttc',
  BTC_MAINNET = 'btc_mainnet',
  BTC = 'btc',
  BTC_SIGNET = 'btc_signet',
  BTC_SIGNET_MAIN = 'btc-signet',
  BTC_BLOCKBOOK = 'btc_blockbook',
  BTC_BLOCKBOOK_SIGNET = 'btc_blockbook_signet',
  BITLAYER = 'bitlayer',
  BITLAYER_TESTNET = 'bitlayer_testnet',
  CELO = 'celo',
  CELO_BAKLAVA = 'celo_baklava_testnet',
  CELO_SEPOLIA = 'celo_sepolia',
  CHILIZ = 'chiliz',
  CHILIZ_TESTNET = 'chiliz_testnet',

  CORE = 'core',

  CORN = 'corn',
  CORN_MAIZENET = 'corn-maizenet',
  CORN_TESTNET = 'corn-testnet',

  DOGEOS = 'dogeos',
  DOGEOS_TESTNET = 'dogeos-testnet',

  ETH = 'eth',
  ETH_BEACON = 'eth_beacon',
  ETH_KOVAN = 'eth_kovan',
  ETH_RINKEBY = 'eth_rinkeby',
  ETH_ROPSTEN = 'eth_ropsten',
  ETH_SEPOLIA = 'eth_sepolia',
  ETH_SEPOLIA_BEACON = 'eth_sepolia_beacon',
  ETH_HOODI = 'eth_hoodi',
  ETH_HOODI_BEACON = 'eth_hoodi_beacon',
  ETH_HOLESKY = 'eth_holesky',
  ETH_HOLESKY_BEACON = 'eth_holesky_beacon',
  ELECTRONEUM = 'electroneum',
  ELECTRONEUM_TESTNET = 'electroneum_testnet',

  ETHERLINK = 'etherlink',
  ETHERLINK_TESTNET = 'etherlink-testnet',
  ETHERLINK_SHADOWNET_TESTNET = 'etherlink-shadownet-testnet',

  FANTOM = 'fantom',
  FANTOM_TESTNET = 'fantom_testnet',
  FILECOIN = 'filecoin',

  FLARE = 'flare',
  FLARE_EVM = 'flare-evm',
  FLARE_C = 'flare-c',
  FLARE_P = 'flare-p',
  FLARE_X = 'flare-x',
  FLARE_COSTON = 'flare_coston',
  FLARE_COSTON_EVM = 'flare_coston-evm',
  FLARE_COSTON_C = 'flare_coston-c',
  FLARE_COSTON_P = 'flare_coston-p',
  FLARE_COSTON_X = 'flare_coston-x',
  FLARE_COSTON2 = 'flare_coston2',
  FLARE_COSTON2_EVM = 'flare_coston2-evm',
  FLARE_COSTON2_C = 'flare_coston2-c',
  FLARE_COSTON2_P = 'flare_coston2-p',
  FLARE_COSTON2_X = 'flare_coston2-x',
  FLARE_SONGBIRD = 'flare_songbird',
  FLARE_SONGBIRD_EVM = 'flare_songbird-evm',
  FLARE_SONGBIRD_C = 'flare_songbird-c',
  FLARE_SONGBIRD_P = 'flare_songbird-p',
  FLARE_SONGBIRD_X = 'flare_songbird-x',

  FUEL = 'fuel',
  FUEL_REST = 'fuel-rest',
  FUEL_SEPOLIA = 'fuel-sepolia',

  GNOSIS = 'gnosis',
  GNOSIS_BEACON = 'gnosis_beacon',
  GNOSIS_TESTNET = 'gnosis_testnet',
  GNOSIS_TESTNET_BEACON = 'gnosis_testnet_beacon',

  GOAT = 'goat',
  GOAT_TESTNET = 'goat-testnet',

  GRAVITY = 'gravity',

  HARMONY = 'harmony',

  HORIZEN = 'horizen',
  HORIZEN_EVM = 'horizen-evm',
  HORIZEN_TESTNET = 'horizen-testnet',
  HORIZEN_TESTNET_EVM = 'horizen-testnet-evm',

  HECO = 'heco',
  HECO_TESTNET = 'heco_testnet',

  HYPEREVM = 'hyperevm',

  IOTA = 'iota',
  IOTA_TESTNET = 'iota-testnet',
  IOTA_EVM = 'iota_evm',
  IOTA_EVM_TESTNET = 'iota-evm-testnet',

  IOTEX = 'iotex',
  IOTEX_TESTNET = 'iotex_testnet',

  KAVA = 'kava',
  KAVA_COSMOS_REST = 'kava-cosmos-rest',
  KAVA_EVM = 'kava-evm',
  KAVA_TENDERMINT_REST = 'kava-tendermint-rest',
  KAVA_TENDERMINT_RPC = 'kava-tendermint-rpc',
  KAVA_TENDERMINT_GRPC = 'kava-tendermint-grpc',

  KAVA_TESTNET = 'kava-testnet',
  KAVA_COSMOS_REST_TESTNET = 'kava-cosmos-rest-testnet',
  KAVA_EVM_TESTNET = 'kava-evm-testnet',
  KAVA_TENDERMINT_REST_TESTNET = 'kava-tendermint-rest-testnet',
  KAVA_TENDERMINT_RPC_TESTNET = 'kava-tendermint-rpc-testnet',

  KITE = 'kite',

  KINTO = 'kinto',
  KAIA = 'kaia',
  KAIA_TESTNET = 'kaia_testnet',
  KUSAMA = 'kusama',

  LINEA = 'linea',

  MANTLE = 'mantle',
  MANTLE_TESTNET = 'mantle_testnet',
  MANTLE_SEPOLIA = 'mantle_sepolia',
  METIS = 'metis',
  MOONBEAM = 'moonbeam',
  MULTICHAIN = 'multichain',

  MONAD = 'monad',
  MONAD_TESTNET = 'monad-testnet',

  MOVEMENT_MAINNET = 'movement_mainnet',
  MOVEMENT_MAINNET_REST = 'movement_mainnet-rest',
  MOVEMENT_BARDOCK = 'movement_bardock',
  MOVEMENT_BARDOCK_REST = 'movement_bardock-rest',

  MATCHAIN = 'matchain',

  MIDNIGHT = 'midnight',
  MIDNIGHT_TESTNET = 'midnight-testnet',

  NEAR = 'near',

  NEURA = 'neura',
  NEURA_TESTNET = 'neura-testnet',

  NERVOS = 'nervos',
  NERVOS_EVM = 'nervos_evm',
  NERVOS_CKB = 'nervos_ckb',
  NERVOS_GW = 'nervos_gw',

  NUBIT = 'nubit',

  XLAYER = 'xlayer',
  XLAYER_TESTNET = 'xlayer_testnet',

  OPTIMISM = 'optimism',
  OPTIMISM_SEPOLIA = 'optimism_sepolia',
  OPTIMISM_TESTNET = 'optimism_testnet',

  PLASMA = 'plasma',

  POLKADOT = 'polkadot',
  POLKADOT_RPC = 'polkadot-rpc',
  POLKADOT_ASSET_HUB = 'polkadot-asset-hub',

  POLYGON = 'polygon',
  POLYGON_MUMBAI = 'polygon_mumbai',
  POLYGON_AMOY = 'polygon_amoy',
  POLYGON_ZKEVM = 'polygon_zkevm',
  POLYGON_ZKEVM_TESTNET = 'polygon_zkevm_testnet',
  POLYGON_ZKEVM_CARDONA = 'polygon_zkevm_cardona',

  REDBELLY = 'redbelly',

  ROBINHOOD = 'robinhood',

  ROLLUX = 'rollux',
  ROLLUX_OPNODE = 'rollux_opnode',
  ROLLUX_OPNODE_TESTNET = 'rollux_opnode_testnet',
  ROLLUX_TESTNET = 'rollux_testnet',

  STORY = 'story',
  STORY_ODYSSEY = 'story-odyssey',
  STORY_AENEID = 'story-aeneid-testnet',

  STATUS = 'status',
  STATUS_TESTNET = 'status-testnet',

  SCROLL = 'scroll',
  SCROLL_TESTNET = 'scroll_testnet',
  SCROLL_SEPOLIA_TESTNET = 'scroll_sepolia_testnet',

  SECRET = 'scrt',
  SECRET_COSMOS = 'scrt_cosmos',
  SECRET_COSMOS_GRPC_WEB = 'scrt-cosmos-grpc-web',
  SECRET_COSMOS_REST = 'scrt-cosmos-rest',
  SECRET_REST = 'scrt-rest',
  SECRET_RPC = 'scrt-rpc',

  SEI = 'sei',
  SEI_EVM = 'sei-evm',
  SEI_COSMOS_REST = 'sei-cosmos-rest',
  SEI_REST = 'sei-rest',
  SEI_RPC = 'sei-rpc',
  SEI_TENDERMINT_GRPC = 'sei-tendermint-grpc',

  SOLANA = 'solana',
  SOLANA_DEVNET = 'solana_devnet',

  SOMNIA = 'somnia',
  SOMNIA_TESTNET = 'somnia-testnet',

  SONIC = 'sonic',
  SONIC_BLAZE_TESTNET = 'sonic-blaze-testnet',

  STELLAR = 'stellar',
  STELLAR_HORIZON = 'stellar-horizon',
  STELLAR_SOROBAN = 'stellar-soroban',
  STELLAR_TESTNET = 'stellar-testnet',
  STELLAR_TESTNET_HORIZON = 'stellar-testnet-horizon',
  STELLAR_TESTNET_SOROBAN = 'stellar-testnet-soroban',

  SWELL = 'swell',
  SWELL_SEPOLIA = 'swell-sepolia',

  SYSCOIN = 'syscoin',

  TAC = 'tac',
  // mainnets
  TAC_EVM = 'tac-evm',
  TAC_TENDERMINT_JSON_RPC = 'tac-rpc',
  TAC_TENDERMINT_GRPC = 'tac-grpc',
  TAC_TENDERMINT_REST = 'tac-rest',
  TAC_COSMOS_REST = 'tac-cosmos-rest',
  TAC_COSMOS_GRPC_WEB = 'tac-cosmos-grpc-web',
  // testnets
  TAC_TESTNET_SPB = 'tac-spb',
  TAC_TESTNET_SPB_GRPC = 'tac-spb-grpc',
  TAC_TESTNET_SPB_COSMOS_GRPC = 'tac-spb-cosmos-grpc-web',
  TAC_TESTNET_SPB_COSMOS_REST = 'tac-spb-cosmos-rest',
  TAC_TESTNET_SPB_EVM = 'tac-spb-evm',
  TAC_TESTNET_SPB_TENDERMINT_REST = 'tac-spb-rest',
  TAC_TESTNET_SPB_TENDERMINT_RPC = 'tac-spb-rpc',

  TAIKO = 'taiko',
  TAIKO_HOODI = 'taiko_hoodi',
  TELOS = 'telos',

  TRON = 'tron',
  TRON_REST = 'tron_rest',
  TRON_JSON_RPC = 'tron_jsonrpc',

  TON = 'ton',
  TON_REST = 'ton-rest',
  TON_RPC = 'ton-rpc',

  TENET = 'tenet',
  TENET_EVM = 'tenet-evm',

  WHITECHAIN = 'whitechain',
  WHITECHAIN_TESTNET = 'whitechain-testnet',

  WORLDCHAIN = 'worldchain',

  XAI = 'xai',
  XAI_TESTNET = 'xai_testnet',
  XDAI = 'xdai',

  // sui mainnets
  SUI = 'sui',
  SUI_GRPC = 'sui-grpc',
  SUI_GRPC_ARCHIVE = 'sui-archive-grpc',
  SUI_GRAPHQL = 'sui-graphql',
  // sui testnets
  SUI_TESTNET = 'sui_testnet',
  SUI_TESTNET_GRPC = 'sui-testnet-grpc',

  XDC = 'xdc',
  XDC_TESTNET = 'xdc_testnet',

  XRP = 'xrp',

  XPHERE = 'xphere',
  XPHERE_TESTNET = 'xphere-testnet',

  UNICHAIN = 'unichain',

  ZETACHAIN = 'zetachain',

  /* Athens-3 */
  ZETACHAIN_COSMOS_REST_ATHENS_TESTNET = 'zetachain-cosmos-rest-athens-testnet',
  ZETACHAIN_EVM_ATHENS_TESTNET = 'zetachain-evm-athens-testnet',
  ZETACHAIN_TENDERMINT_REST_ATHENS_TESTNET = 'zetachain-tendermint-rest-athens-testnet',
  ZETACHAIN_TENDERMINT_RPC_ATHENS_TESTNET = 'zetachain-tendermint-rpc-athens-testnet',
  ZETACHAIN_ATHENS_TESTNET = 'zetachain-athens-testnet',

  ZKSYNC_ERA = 'zksync_era',
  ZKSYNC_ERA_TESTNET = 'zksync_era_testnet',
  ZKSYNC_ERA_SEPOLIA = 'zksync_era_sepolia',

  ZKVERIFY = 'zkverify',
  ZKVERIFY_MAINNET = 'zkverify-mainnet',
  ZKVERIFY_TESTNET = 'zkverify-testnet',
  ZKVERIFY_VOLTA_TESTNET = 'zkverify-volta-testnet',
  ZKVERIFY_VFLOW_PARACHAIN_MAINNET = 'zkverify-vflow-parachain',
  ZKVERIFY_VFLOW_PARACHAIN_TESTNET = 'zkverify-vflow-parachain-testnet',

  UNDEFINED = '',
}

export const TAC_CHAINS = [
  ChainID.TAC,
  ChainID.TAC_EVM,
  ChainID.TAC_TENDERMINT_JSON_RPC,
  ChainID.TAC_TENDERMINT_REST,
  ChainID.TAC_COSMOS_REST,
  ChainID.TAC_COSMOS_GRPC_WEB,
  ChainID.TAC_TESTNET_SPB,
  ChainID.TAC_TESTNET_SPB_COSMOS_GRPC,
  ChainID.TAC_TESTNET_SPB_COSMOS_REST,
  ChainID.TAC_TESTNET_SPB_EVM,
  ChainID.TAC_TESTNET_SPB_TENDERMINT_REST,
  ChainID.TAC_TESTNET_SPB_TENDERMINT_RPC,
];

export const ZETACHAIN_ATHENS3_CHAINS = [
  ChainID.ZETACHAIN_COSMOS_REST_ATHENS_TESTNET,
  ChainID.ZETACHAIN_EVM_ATHENS_TESTNET,
  ChainID.ZETACHAIN_TENDERMINT_REST_ATHENS_TESTNET,
  ChainID.ZETACHAIN_TENDERMINT_RPC_ATHENS_TESTNET,
];

export const FLARE_TESTNETS = [
  ChainID.FLARE_COSTON,
  ChainID.FLARE_COSTON_EVM,
  ChainID.FLARE_COSTON_C,
  ChainID.FLARE_COSTON_P,
  ChainID.FLARE_COSTON_X,
  ChainID.FLARE_COSTON2,
  ChainID.FLARE_COSTON2_EVM,
  ChainID.FLARE_COSTON2_C,
  ChainID.FLARE_COSTON2_P,
  ChainID.FLARE_COSTON2_X,
  ChainID.FLARE_SONGBIRD,
  ChainID.FLARE_SONGBIRD_EVM,
  ChainID.FLARE_SONGBIRD_C,
  ChainID.FLARE_SONGBIRD_P,
  ChainID.FLARE_SONGBIRD_X,
];

export const TAC_TESTNETS = [
  ChainID.TAC_TESTNET_SPB,
  ChainID.TAC_TESTNET_SPB_COSMOS_GRPC,
  ChainID.TAC_TESTNET_SPB_COSMOS_REST,
  ChainID.TAC_TESTNET_SPB_EVM,
  ChainID.TAC_TESTNET_SPB_GRPC,
  ChainID.TAC_TESTNET_SPB_TENDERMINT_REST,
  ChainID.TAC_TESTNET_SPB_TENDERMINT_RPC,
];

export const FLARE_CHAINS = [
  ChainID.FLARE,
  ChainID.FLARE_EVM,
  ChainID.FLARE_C,
  ChainID.FLARE_P,
  ChainID.FLARE_X,
  ...FLARE_TESTNETS,
];

export const ADVANCED_API_CHAINS = {
  mainnets: [
    ChainID.AVALANCHE,
    ChainID.ARBITRUM,
    ChainID.BASE,
    ChainID.BSC,
    ChainID.ETH,
    ChainID.FANTOM,
    ChainID.FLARE,
    ChainID.GNOSIS,
    ChainID.LINEA,
    ChainID.OPTIMISM,
    ChainID.POLYGON,
    ChainID.SCROLL,
    ChainID.SOMNIA,
    // Served by the stellar_* namespace, not as a blockchain value on ankr_*.
    ChainID.STELLAR,
    ChainID.TAIKO,
    ChainID.TELOS,
    ChainID.XLAYER,
  ],
  testnets: [
    ChainID.AVALANCHE_FUJI,
    ChainID.POLYGON_AMOY,
    ChainID.BASE_SEPOLIA,
    ChainID.ETH_SEPOLIA,
    ChainID.OPTIMISM_SEPOLIA,
  ],
};
